import { AlertTriangle, ArrowRight, Download, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { dateFr } from '../utils/dates';

/**
 * Ce que l'espace annonce quand il n'accepte plus de saisie.
 *
 * Le serveur refusait déjà les écritures et disait pourquoi — mais il ne le
 * disait qu'au moment du refus, c'est-à-dire après qu'on ait rempli un
 * formulaire. On apprenait que la saisie était close en perdant ce qu'on
 * venait d'écrire, ce qui est la plus mauvaise façon de l'apprendre.
 *
 * Le bandeau se pose donc en haut de chaque écran. Trois situations, un ton
 * différent pour chacune : un impayé n'est pas une faute, une archive n'est
 * pas une menace, et une résiliation est une décision qu'on respecte — on
 * n'y fait pas la leçon, on dit jusqu'à quand les données restent là et à
 * quelles conditions on peut revenir.
 */
export default function BandeauExploitation() {
  const { exploitation } = useAuth();

  if (!exploitation) {
    return null;
  }

  const resilie = exploitation.etat === 'resilie';
  const jours = exploitation.joursRestants;

  // Le rouge quand la destruction approche, l'ambre le reste du temps : une
  // couleur d'alarme portée trois mois durant ne veut plus rien dire le jour
  // où elle compte.
  const urgent = jours !== null && jours <= 15;
  const teinte = urgent
    ? 'bg-red-50 border-red-200 text-red-900'
    : 'bg-amber-50 border-amber-200 text-amber-900';

  return (
    <div className={`border-b px-4 py-3 ${teinte}`} role="status">
      <div className="max-w-5xl mx-auto flex items-start gap-3">
        {resilie ? (
          <Lock className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
        ) : (
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
        )}

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            {resilie ? 'Abonnement résilié — saisie close' : 'Saisie suspendue'}
            {jours !== null && (
              <>
                {' · '}
                {jours === 0
                  ? "suppression aujourd'hui"
                  : `${jours} jour${jours > 1 ? 's' : ''} avant suppression`}
                {exploitation.echeance && ` (le ${dateFr(exploitation.echeance)})`}
              </>
            )}
          </p>
          <p className="mt-0.5 text-sm leading-snug">{exploitation.message}</p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {/* L'export d'abord, et toujours : c'est le geste qui reste
                possible quoi qu'il arrive, et le seul qui ne se rattrape pas
                une fois la date passée. */}
            <Link
              to="/admin/donnees"
              className="inline-flex items-center gap-1.5 rounded-lg border border-current/30 bg-white/60 px-2.5 py-1.5 text-sm font-semibold hover:bg-white"
            >
              <Download className="w-4 h-4" /> Emporter mes données
            </Link>

            {exploitation.offre && (
              <a
                href={exploitation.offre}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-2.5 py-1.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Revenir avec 30 % de remise <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
